import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../contexts/TranslationContext';
import { useAuth } from '../../contexts/AuthContext';
import { settingsApi } from '../../api';
import SEO from '../../components/SEO';
import { getFullUrl } from '../../utils/constants';
import HeroSection from './components/HeroSection';
import FeaturesSection from './components/FeaturesSection';
import TestimonialsSection from './components/TestimonialsSection';
import TrustSignalsSection from './components/TrustSignalsSection';
import Footer from '../../components/ui/Footer';
import LanguageThemeToggle from './components/LanguageThemeToggle';
import type {
  HeroContent,
  Feature,
  Testimonial,
  TrustSignal,
  FooterSection } from
'./types';

const LandingPage = () => {
  const navigate = useNavigate();
  const { t, locale, isLoading: translationsLoading } = useTranslation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [partners, setPartners] = useState<TrustSignal[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  
  // Redirect to dashboard if user is already logged in
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/timeline', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);
  
  // Fallback translations in case API fails or translations are loading
  const getTranslation = (key: string, fallback: string): string => {
    if (translationsLoading) {
      return fallback; // Show fallback while loading
    }
    const translation = t(key);
    // If translation equals key, it means translation not found, use fallback
    return translation === key ? fallback : translation;
  };

  // Helper to convert relative URL to full URL
  const getFullUrl = (url: string): string => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    const apiBaseUrl = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8000/api/v1';
    const baseUrl = apiBaseUrl.replace('/api/v1', '');
    return `${baseUrl}/${url.replace(/^\//, '')}`;
  };

  // Load partners and testimonials from settings
  useEffect(() => {
    const loadContent = async () => {
      try {
        const [partnersSetting, testimonialsSetting] = await Promise.all([
          settingsApi.getPublicSetting('landing_partners').catch(() => null),
          settingsApi.getPublicSetting('landing_testimonials').catch(() => null),
        ]);

        if (partnersSetting?.setting?.value) {
          try {
            // Value might already be decoded (if type is 'json') or still a string
            let partnersData = partnersSetting.setting.value;
            if (typeof partnersData === 'string') {
              partnersData = JSON.parse(partnersData);
            }
            // Ensure all logo URLs are full URLs
            const processedPartners = Array.isArray(partnersData) 
              ? partnersData.map((p: TrustSignal) => ({
                  ...p,
                  logo: p.logo ? getFullUrl(p.logo) : p.logo
                }))
              : [];
            setPartners(processedPartners);
          } catch (e) {
            console.error('Failed to parse partners:', e, partnersSetting.setting.value);
            setPartners([]);
          }
        } else {
          setPartners([]);
        }

        if (testimonialsSetting?.setting?.value) {
          try {
            // Value might already be decoded (if type is 'json') or still a string
            let testimonialsData = testimonialsSetting.setting.value;
            if (typeof testimonialsData === 'string') {
              testimonialsData = JSON.parse(testimonialsData);
            }
            // Ensure all image URLs are full URLs
            const processedTestimonials = Array.isArray(testimonialsData)
              ? testimonialsData.map((t: Testimonial) => ({
                  ...t,
                  image: t.image ? getFullUrl(t.image) : t.image
                }))
              : [];
            setTestimonials(processedTestimonials);
          } catch (e) {
            console.error('Failed to parse testimonials:', e, testimonialsSetting.setting.value);
            setTestimonials([]);
          }
        } else {
          setTestimonials([]);
        }
      } catch (error) {
        console.error('Failed to load landing page content:', error);
      }
    };

    loadContent();
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Sisi ni Tanzania",
    "description": "Tanzania's Leading Youth Digital Engagement Platform",
    "url": getFullUrl(""),
    "logo": getFullUrl("/assets/images/logo.png"),
    "sameAs": [
      "https://www.facebook.com/sisinitanzania",
      "https://twitter.com/sisinitanzania",
      "https://www.linkedin.com/company/sisinitanzania"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
      "areaServed": "TZ",
      "availableLanguage": ["en", "sw"]
    },
    "areaServed": {
      "@type": "Country",
      "name": "Tanzania"
    },
    "audience": {
      "@type": "Audience",
      "audienceType": "Youth"
    }
  };

  const heroContent: HeroContent = useMemo(() => ({
    headline: getTranslation('landing.hero.headline', 'Amplify Your Voice, Shape Your Future'),
    subheadline: getTranslation('landing.hero.subheadline', "Join Tanzania's Leading Youth Digital Engagement Platform"),
    description: getTranslation('landing.hero.description', 'Connect with thousands of young changemakers, participate in meaningful discussions, attend impactful events, and unlock opportunities for personal and professional growth.'),
    primaryCTA: getTranslation('landing.hero.primaryCTA', 'Join Free Today'),
    secondaryCTA: getTranslation('landing.hero.secondaryCTA', 'Sign In')
  }), [t, translationsLoading]);

  const features: Feature[] = useMemo(() => [
    {
      id: "1",
      icon: "MessageSquare",
      title: getTranslation('landing.features.forum.title', 'Forum Discussions'),
      description: getTranslation('landing.features.forum.description', 'Engage in vibrant conversations on topics that matter. Share ideas, ask questions, and connect with peers across Tanzania.')
    },
    {
      id: "2",
      icon: "Calendar",
      title: getTranslation('landing.features.events.title', 'Event Participation'),
      description: getTranslation('landing.features.events.description', 'Discover and register for workshops, webinars, and networking events designed to boost your skills and career.')
    },
    {
      id: "3",
      icon: "Handshake",
      title: getTranslation('landing.features.partners.title', 'Partner Collaboration'),
      description: getTranslation('landing.features.partners.description', 'Connect with verified NGOs, government agencies, and private sector partners offering real opportunities.')
    },
    {
      id: "4",
      icon: "Award",
      title: getTranslation('landing.features.achievements.title', 'Achievement Recognition'),
      description: getTranslation('landing.features.achievements.description', 'Earn badges and recognition for your contributions, participation, and milestones on the platform.')
    }
  ], [t, translationsLoading]);


  const displayTestimonials = testimonials;


  // Use partners from settings, fallback to default if empty
  // Data URI placeholder to avoid 404 errors
  const placeholderLogo = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='100'%3E%3Crect fill='%23e5e7eb' width='200' height='100'/%3E%3Ctext fill='%239ca3af' font-family='sans-serif' font-size='12' x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle'%3ELogo%3C/text%3E%3C/svg%3E";
  
  const defaultPartners: TrustSignal[] = [
    {
      id: "1",
      name: "Ministry of Youth Affairs",
      logo: placeholderLogo,
      alt: "Official government seal logo with Tanzanian national colors and coat of arms representing Ministry of Youth Affairs",
      type: "government"
    },
    {
      id: "2",
      name: "Tanzania Youth Network",
      logo: placeholderLogo,
      alt: "Circular logo with interconnected youth figures in green and blue representing Tanzania Youth Network organization",
      type: "ngo"
    },
    {
      id: "3",
      name: "Youth Empowerment Certificate",
      logo: placeholderLogo,
      alt: "Gold certification badge with ribbon and checkmark symbol representing youth empowerment accreditation",
      type: "certification"
    },
    {
      id: "4",
      name: "African Youth Alliance",
      logo: placeholderLogo,
      alt: "Stylized African continent logo in orange and green colors representing African Youth Alliance partnership",
      type: "ngo"
    },
    {
      id: "5",
      name: "Digital Skills Certified",
      logo: placeholderLogo,
      alt: "Blue digital badge with computer icon and stars representing digital skills certification program",
      type: "certification"
    },
    {
      id: "6",
      name: "UN Youth Program",
      logo: placeholderLogo,
      alt: "United Nations emblem in blue with olive branches representing UN Youth Program partnership",
      type: "government"
    }
  ];

  const displayPartners = partners.length > 0 ? partners : defaultPartners;


  const footerSections: FooterSection[] = useMemo(() => [
    {
      title: getTranslation('landing.footer.platform.title', 'Platform'),
      links: [
        { label: getTranslation('landing.footer.platform.forums', 'Forums'), path: "/forums" },
        { label: getTranslation('landing.footer.platform.events', 'Events'), path: "/events" },
        { label: getTranslation('landing.footer.platform.blog', 'Blog'), path: "/blog" },
        { label: getTranslation('landing.footer.platform.partners', 'Partners'), path: "/partners/apply" }
      ]
    },
    {
      title: getTranslation('landing.footer.resources.title', 'Resources'),
      links: [
        { label: getTranslation('landing.footer.resources.help', 'Help Center'), path: "#" },
        { label: getTranslation('landing.footer.resources.guidelines', 'Community Guidelines'), path: "#" },
        { label: getTranslation('landing.footer.resources.safety', 'Safety Tips'), path: "#" },
        { label: getTranslation('landing.footer.resources.faqs', 'FAQs'), path: "#" }
      ]
    },
    {
      title: getTranslation('landing.footer.company.title', 'Company'),
      links: [
        { label: getTranslation('landing.footer.company.about', 'About Us'), path: "#" },
        { label: getTranslation('landing.footer.company.contact', 'Contact'), path: "#" },
        { label: getTranslation('landing.footer.company.careers', 'Careers'), path: "#" },
        { label: getTranslation('landing.footer.company.press', 'Press Kit'), path: "#" }
      ]
    }
  ], [t, translationsLoading]);


  const handleRegisterClick = () => {
    navigate('/login');
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  // Don't render landing page if user is authenticated (will redirect)
  if (!authLoading && isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Sisi ni Tanzania"
        description="Join Tanzania's Leading Youth Digital Engagement Platform. Connect with thousands of young changemakers, participate in meaningful discussions, attend impactful events, and unlock opportunities for personal and professional growth."
        keywords="Tanzania, youth platform, digital engagement, community, events, opportunities, forum, blog, networking, youth empowerment, Tanzania youth, community platform"
        url={getFullUrl("")}
        structuredData={structuredData}
      />
      <LanguageThemeToggle />
      
      <HeroSection
        content={heroContent}
        onRegisterClick={handleRegisterClick}
        onLoginClick={handleLoginClick} />


      <FeaturesSection features={features} />

      <TestimonialsSection testimonials={displayTestimonials} />

      <TrustSignalsSection trustSignals={displayPartners} />

      <Footer />
    </div>);

};

export default LandingPage;